import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clock, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import api, { errMsg } from "@/lib/api";

export function nextOccurrence(weekday, time) {
  const [h, m] = time.split(":").map(Number);
  const now = new Date();
  const d = new Date(now);
  d.setHours(h, m, 0, 0);
  d.setDate(d.getDate() + ((weekday - ((now.getDay() + 6) % 7) + 7) % 7));
  if (d <= now) d.setDate(d.getDate() + 7);
  return d;
}

const fmt12 = (t) => { const [h, m] = t.split(":").map(Number); return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`; };

export default function BestTimePanel({ groupIds, onPick }) {
  const [enabled, setEnabled] = useState(null);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setEnabled(r.data.ai_best_time)).catch(() => setEnabled(false)); }, []);

  const load = async (refresh = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai/best-times", { params: { tz_offset: new Date().getTimezoneOffset(), refresh } });
      setEnabled(data.enabled);
      setItems(data.items);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (enabled === null) return null;
  if (!enabled)
    return <p className="mt-4 text-xs text-muted-foreground" data-testid="best-time-disabled">Cadangan masa terbaik AI dimatikan. <Link to="/tetapan" className="text-primary hover:underline">Hidupkan di Tetapan</Link></p>;

  const shown = (items || []).filter((i) => groupIds.length === 0 || groupIds.includes(i.group_id));
  return (
    <div className="mt-4 rounded-xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="best-time-panel">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-violet-300"><Sparkles className="h-4 w-4" /> Masa terbaik (AI)</p>
        {items && <button onClick={() => load(true)} disabled={busy} className="text-violet-300 hover:text-white" data-testid="best-time-refresh-btn"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>}
      </div>
      {!items ? (
        <Button size="sm" onClick={() => load()} disabled={busy} className="mt-3 bg-violet-600 hover:bg-violet-500" data-testid="best-time-btn">
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Clock className="mr-1.5 h-3.5 w-3.5" />} Cadangkan masa terbaik
        </Button>
      ) : (
        <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
          {shown.length === 0 && <p className="text-xs text-muted-foreground">Tiada cadangan untuk group dipilih.</p>}
          {shown.map((i) => (
            <div key={i.group_id} className="rounded-lg border border-border bg-background/60 p-3" data-testid={`best-time-item-${i.group_id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium">{i.name}</p>
                <Button size="sm" variant="outline" className="h-7 shrink-0 border-violet-500/40 px-2 text-xs text-violet-200" onClick={() => onPick(nextOccurrence(i.weekday, i.time))} data-testid={`best-time-use-btn-${i.group_id}`}>Guna</Button>
              </div>
              <p className="mt-1 font-mono text-xs text-violet-200">{i.day_name}, {fmt12(i.time)}</p>
              <p className="mt-1 text-xs text-muted-foreground">{i.reason}</p>
              <p className="mt-1 text-[10px] text-muted-foreground">{i.based_on >= 3 ? `Berdasarkan ${i.based_on} post selesai` : "Data belum cukup — guna amalan umum"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
